import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, mergeMap, Observable, of, tap } from 'rxjs';
import { SurplusLinesTokenValidation } from '../models/surplus-lines-token-validation.model';
import { environment } from '../../environments/environment';
import { SurplusLinesRegistrationModel } from '../models/surplus-lines-registration.model';
import { EmailCodeResult, SurplusLinesOpportunity } from '../models/surplus-lines-opportunity.model';
import { SecureHttp } from '../helpers/securehttp';
import { SurplusLinesDocument, SurplusLinesDocumentUpload } from '../models/surplus-lines-document.model';
import { SurplusLinesDocumentUploadResponse } from '../models/surplus-lines-document-upload-response.model';
import { SurplusLinesOpportunitySnapshot, SurplusLinesUserId } from '../models/surplus-lines-opportunity-snapshot.model';
import { StatusResponse } from '../models/status-response.model';
import { SurplusLinesPlanDesign } from '../models/surplus-lines-plan-design.model';

@Injectable({
  providedIn: 'root'
})
export class OpportunitiesService {
  token: SurplusLinesTokenValidation | undefined;
  user: any | undefined;

  constructor(private _http: HttpClient,
    private secureHttp: SecureHttp
  ) {

  }

  get CurrentUser(): any | undefined {
    return JSON.parse(localStorage.getItem('User') as string);
  }

  get CurrentUserId(): any | undefined {
    return localStorage.getItem('UserId') as string;
  }

  get CurrentEmailCode(): EmailCodeResult | undefined {
    return JSON.parse(localStorage.getItem('EmailCode') as string);
  }

  get CurrentValidatedToken(): SurplusLinesTokenValidation | undefined {
    return JSON.parse(localStorage.getItem('ValidatedToken') as string);
  }

  get CurrentOpportunity(): SurplusLinesOpportunity | undefined {
    return JSON.parse(localStorage.getItem('Opportunity') as string);
  }

  validateToken(token: string): Observable<SurplusLinesTokenValidation> {
    localStorage.setItem('sponsorToken', token.toLowerCase());
    let url = `${environment.apiUrl}api/surplus-lines/token/validate?token=${token}`;

    return this._http.get(url).pipe(
      tap(response => localStorage.setItem('ValidatedToken', JSON.stringify(response))),
      map(response => response as SurplusLinesTokenValidation));
  }

  createAccount(model: SurplusLinesRegistrationModel): Observable<SurplusLinesOpportunity> {
    let url = `${environment.apiUrl}api/surplus-lines/token/create-account`;

    return this._http.post(url, model).pipe(
      tap(response => localStorage.setItem('User', JSON.stringify(response))),
      map(response => response as SurplusLinesOpportunity));
  }
  
  emailExists(email: string): Observable<string> {
    let url = `${environment.apiUrl}api/surplus-lines/email-exists/${email}`;

    return this._http.post(url, {}).pipe(
      tap(response => localStorage.setItem('UserId', (response as SurplusLinesUserId).userId as string)),
      map(response => response as string));
  }

  sendEmail(email: string): Observable<EmailCodeResult> {
    let url = `${environment.apiUrl}api/surplus-lines/email-code/${email}`;

    return this._http.post(url, {}).pipe(
            tap(response => {
              localStorage.setItem('EmailCode', JSON.stringify(response as EmailCodeResult))
            }),
            map(response => new EmailCodeResult() as EmailCodeResult));
  }

  checkCode(code: string, email: string): Observable<boolean> {
    let url = `${environment.apiUrl}api/surplus-lines/email-code/check`;
    let params = new HttpParams();
    params = params.set('code', code);
    params = params.set('email', email);
    
    return this._http.post(url, { params: params }).pipe(
      map(response => response as boolean),
      catchError(x => of(false))
    );
  }

  ExistingUserWithNoApplication(email: string): Observable<string> {
    let url = `${environment.apiUrl}api/surplus-lines/user-exists`;

    return this._http.post(url,  JSON.stringify(email), {
        headers: { 'Content-Type': 'application/json' }
      } ).pipe(
      map(response => (response as SurplusLinesUserId).userId));
  }

  getDocuments(opportunityId: number | string) {
    let url = `${environment.apiUrl}api/surplus-lines/opportunities/${opportunityId}/documents`;
    return this.secureHttp.get(url).pipe(
      map(response => response as SurplusLinesDocument[]));
  }

  saveDocument(opportunityId: number, document: SurplusLinesDocument): Observable<SurplusLinesDocumentUploadResponse> {
    let url = `${environment.apiUrl}api/surplus-lines/opportunities/${opportunityId}/documents/save`;

    return this.secureHttp.post(url, document).pipe(
      map(response => response as SurplusLinesDocumentUploadResponse));
  }


  AddApplicant(userId: string): Observable<boolean> {
    let url = `${environment.apiUrl}api/surplus-lines/addApplicant`;
    
    return this._http.post(url,  JSON.stringify(userId), {
        headers: { 'Content-Type': 'application/json' }
      } ).pipe(
      map(response => response as boolean));
  }

  uploadDocument(file: File, opportunityId: number, documentId: number, documentTypeCode: string): Observable<SurplusLinesDocumentUploadResponse> {

    let reader = new FileReader();

    let url = `${environment.apiUrl}api/surplus-lines/opportunities/${opportunityId}/documents`;

    let model = {
      DocumentId: documentId,
      DocumentTypeCode: documentTypeCode,
      FileName: file.name.split('.')[0],
      Extension: file.name.split('.')[1]
    } as SurplusLinesDocumentUpload

    return new Observable(observer => {

      let responses = [];

      reader.onload = () => {
        observer.next(reader.result);
        observer.complete();
      };

      reader.onerror = () => {
        observer.error(`An error occurred while uploading file "${file.name}"`);
      }

      reader.readAsDataURL(file);

    }).pipe(mergeMap(content => {
      model.Content = content as string;
      return this.secureHttp.post(url, model);
    })).pipe(map(response => response as SurplusLinesDocumentUploadResponse));
  }

  sendApplication(snapshot: SurplusLinesOpportunitySnapshot, complete: boolean, partA: boolean): Observable<StatusResponse> {
    let url = `${environment.apiUrl}api/surplus-lines/opportunities/${snapshot.OpportunityId}/applications/${complete}`;

    let params = new HttpParams();
    params = params.set('complete', complete.toString());
    params = params.set('partA', partA.toString());

    return this.secureHttp.post(url, snapshot, { params: params }).pipe(
      map(response => response as StatusResponse))
  }

  submit(snapshot: SurplusLinesOpportunitySnapshot): Observable<StatusResponse> {
    let url = `${environment.apiUrl}api/surplus-lines/opportunities/${snapshot.OpportunityId}/submit`;

    return this.secureHttp.post(url, snapshot).pipe(
      tap(response => localStorage.setItem('Opportunity', JSON.stringify(response))),
      map(response => response as StatusResponse))
  }

  selectPlanDesign(insuredId: number, opportunityId: number, planDesignId: number, ptdElectedBenefit: number, ttdElectedBenefit: number) {
    let url = `${environment.apiUrl}api/surplus-lines/opportunities/plan-designs/select`;

    return this.secureHttp.put(url, { InsuredId: +insuredId, OpportunityId: opportunityId, PlanDesignId: planDesignId, PTDElectedBenefit: ptdElectedBenefit, TTDElectedBenefit: ttdElectedBenefit });
  }

  getPlanDesignRating(insuredId: number, opportunityId: number, planDesignId: number, ptdElectedBenefit: number, ttdElectedBenefit: number) {
    let url = `${environment.apiUrl}api/surplus-lines/opportunities/plan-designs/rating`;

    return this.secureHttp.put(url, { InsuredId: insuredId, OpportunityId: opportunityId, PlanDesignId: planDesignId, PTDElectedBenefit: ptdElectedBenefit, TTDElectedBenefit: ttdElectedBenefit }).pipe(
      map(response => response as SurplusLinesPlanDesign));
  }

  checkState(stateCode: string): Observable<boolean> {
    let url = `${environment.apiUrl}api/surplus-lines/opportunities/state/${stateCode}`;

    return this.secureHttp.get(url).pipe(
      map(response => response.Data as boolean));
  }

  checkAge(birthDate: string): Observable<boolean> {
    let formattedDate = new Date(birthDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    let url = `${environment.apiUrl}api/surplus-lines/opportunities/age/${formattedDate}`;

    return this.secureHttp.get(url).pipe(
      map(response => response.Data as boolean));
  }

  deleteApplication(email: string) : Observable<boolean> {
    let formattedDate = new Date(email).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    let url = `${environment.apiUrl}api/surplus-lines/opportunities/age/${formattedDate}`;

    return this.secureHttp.get(url).pipe(
      map(response => response.Data as boolean));
  }
}